import _ from 'lodash';
import { useMemo } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { selectEventChanges, useReportStore } from '@/entities/report/@x/beat';
import type { IEventInfo, IRhythmEventCount } from '@/shared/model';

/**
 * Turns raw rhythm event counts into per-type review progress for the beats and heart-rate tabs. Marks a type fully reviewed when its count is zero, otherwise adjusts the server's reviewed tally by the local event changes whose oldType matches and whose review flag was toggled, then reports progress as a percentage to two decimals.
 *
 * @param holterRhythmEventsCount - Server counts and reviewed tallies keyed by rhythm event type.
 * @param eventChanges - Local, unsaved event edits carrying oldType, isReviewed and oldReviewed.
 * @returns A map of event type to its progress percentage and count.
 */
const computeEventsInfoBeatHr = (
  holterRhythmEventsCount: IRhythmEventCount,
  eventChanges: any[],
): IEventInfo => {
  const reviewProgress: any = {};
  _.forEach(Object.keys(holterRhythmEventsCount), (eventType) => {
    const eventCount = holterRhythmEventsCount[eventType as keyof IRhythmEventCount]?.count;
    // No event => Mark as Reviewed
    if (eventCount === 0) {
      reviewProgress[eventType] = {
        progress: 100,
        count: eventCount,
      };
    } else {
      // Count review in event changes
      let calculateReviewed =
        holterRhythmEventsCount[eventType as keyof IRhythmEventCount]?.reviewed;
      const formatEventType = _.snakeCase(eventType).toUpperCase();
      _.forEach(eventChanges, (x) => {
        if (x.oldType === formatEventType && x.isReviewed !== x.oldReviewed) {
          calculateReviewed += x.oldReviewed ? -1 : 1;
        }
      });
      reviewProgress[eventType] = {
        progress: Number(((calculateReviewed * 100) / eventCount).toFixed(2)),
        count: eventCount,
      };
    }
  });
  return reviewProgress;
};

/**
 * Subscribes the beats and heart-rate tabs to their per-type review progress. Reads rhythmEventCount and the first tab's event changes from the report store and memoises the computed progress map against both.
 *
 * @returns The current review progress map keyed by event type.
 */
export const useEventsInfoBeatHr = (): IEventInfo => {
  const holterRhythmEventsCount = useReportStore((s) => s.rhythmEventCount);
  const eventChanges = useReportStore(selectEventChanges(KEY_RECOIL.BEAT_HR));
  return useMemo(
    () => computeEventsInfoBeatHr(holterRhythmEventsCount, eventChanges),
    [holterRhythmEventsCount, eventChanges],
  );
};

/**
 * Exposes a non-reactive read of the beats and heart-rate review progress for callers outside render. Returns a getter that pulls rhythmEventCount and the first tab's event changes straight from the report store and recomputes the progress map on every call.
 *
 * @returns A getter returning the current review progress map.
 */
export const useReadEventsInfoBeatHr = () => (): IEventInfo => {
  const holterRhythmEventsCount = useReportStore.getState().rhythmEventCount;
  const eventChanges = selectEventChanges(KEY_RECOIL.BEAT_HR)(useReportStore.getState());
  return computeEventsInfoBeatHr(holterRhythmEventsCount, eventChanges);
};
